import React, { useState } from 'react';
import { Phone, MessageCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type Props = {
  /** Aranacak telefon numarası (örn: +902561234567) */
  phone?: string;
  /** WhatsApp'ta açılacak numara (örn: +902561234567) */
  whatsapp?: string;
  /** WhatsApp ön tanımlı mesajı */
  whatsappMessage?: string;
};

function digitsOnly(input: string) {
  return input.replace(/[^\d]/g, '');
}

function buildWaLink(whatsapp: string, text: string) {
  const num = digitsOnly(whatsapp);
  const encoded = encodeURIComponent(text);
  return `https://wa.me/${num}?text=${encoded}`;
}

export default function FloatingActionButtons({
  phone = '+902561234567',
  whatsapp = '+902561234567',
  whatsappMessage = 'Merhaba, Nazilli Family Apart hakkında bilgi almak istiyorum.',
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleWhatsApp = () => {
    const url = buildWaLink(whatsapp, whatsappMessage);
    window.open(url, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${phone}`;
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="mb-4 flex flex-col gap-3 "
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            {/* WhatsApp */}
            <motion.button
              type="button"
              onClick={handleWhatsApp}
              className="group flex items-center gap-3 rounded-full bg-green-500 px-4 py-3 text-white shadow-lg transition-colors hover:bg-green-600"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              aria-label="WhatsApp ile yaz"
            >
              <MessageCircle className="h-5 w-5" />
              <span className="whitespace-nowrap text-sm font-medium">WhatsApp</span>
            </motion.button>

            {/* Ara */}
            <motion.button
              type="button"
              onClick={handleCall}
              className="group flex items-center gap-3 rounded-full bg-blue-500 px-4 py-3 text-white shadow-lg transition-colors hover:bg-blue-600"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              aria-label="Telefon et"
            >
              <Phone className="h-5 w-5" />
              <span className="whitespace-nowrap text-sm font-medium">Ara</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle */}
      <motion.button
        type="button"
        onClick={() => setIsExpanded((p) => !p)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-white shadow-sm  hover:bg-primary/90"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        animate={{ rotate: isExpanded ? 45 : 0 }}
        transition={{ duration: 0.2 }}
        aria-label={isExpanded ? 'Kapat' : 'Hızlı aksiyonları aç'}
      >
        <AnimatePresence mode="wait">
          {isExpanded ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span
              key="phone"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Phone className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
